import React, { PureComponent, useState, useEffect, useRef } from 'react';
import moment from 'moment';

import TimePicker from './TimePicker/TimePicker';
import { hour_list } from 'common/utils/constants';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { DayPicker } from 'components/shared/ReactDayPicker';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import { IconLoader } from 'common/IconLoader';

import { getTimeZone } from 'common/utils/helpers';
import {
  getPropsTime,
  getTime,
  datesComparison
} from './utils';
import { TIME_PERIOD, TIME_FOR_EQUAL_DAYS, TIME_DEFAULT } from './constants';

import './CalendarFilter.css';

class DualCalendar extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      startDay: null,
      endDay: null,
      startText: '',
      endText: '',
      startWindowActive: true,
      endWindowActive: false,
      dateValid: true,
      selectEndNext: false, //true if next click on calendar will select end date,
      isDateOutOfBounds: false,
      time: TIME_DEFAULT,
      isDaysEqual: false,
      isStartDayError: false,
      isEndDayError: false
    };

    this.handleDayClick = this.handleDayClick.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTimePickerChange = this.handleTimePickerChange.bind(this);
  }

  handleClear() {
    this.setState({
      startDay: null,
      endDay: null,
      startText: '',
      endText: '',
      startWindowActive: true,
      endWindowActive: false,
      dateValid: true,
      selectEndNext: false, //true if next click on calendar will select end date,
      time: TIME_DEFAULT,
      isStartDayError: false,
      isEndDayError: false
    });
  }

  handleSubmit() {
    if (this.state.isStartDayError || this.state.isEndDayError) {
      return;
    }

    // Save days` data
    if (this.state.startDay && this.state.endDay && !this.props.isTimeIncluded) {
      this.props.handleSubmit(this.state.startDay, this.state.endDay);
    }

    // Time picker is included
    if (this.state.startDay && this.state.endDay && this.props.isTimeIncluded) {
      const startDay = new Date(this.state.startDay);
      startDay.setHours(+this.state.time.start.hours);
      startDay.setMinutes(+this.state.time.start.minutes);

      const endDay = new Date(this.state.endDay);
      endDay.setHours(+this.state.time.end.hours);
      endDay.setMinutes(+this.state.time.end.minutes);

      this.props.handleSubmit(startDay, endDay);
    }
  }

  handleDayClick(clickDay) {
    this.setState({ isDateOutOfBounds: false });
    clickDay.setHours(0, 0, 0, 0);
    let day = clickDay;
    let mmnt = moment(day.toISOString());

    if (this.state.selectEndNext) {
      let mmnt2 = moment(this.state.startDay.toISOString());

      if (mmnt.isBefore(mmnt2)) {
        // Set the second day ( before the first one )
        this.setState({
          endWindowActive: false,
          startWindowActive: false,
          startDay: day,
          endDay: this.state.startDay,
          startText: mmnt.format('MMM D, YYYY'),
          endText: mmnt2.format('MMM D, YYYY'),
          selectEndNext: false,
          time: TIME_DEFAULT,
          isDaysEqual: false
        });
      } else {
        // Set the second day ( after the first one )
        this.setState({
          endWindowActive: false,
          startWindowActive: false,
          endDay: day,
          endText: mmnt.format('MMM D, YYYY'),
          selectEndNext: false,
          time: TIME_DEFAULT,
          isDaysEqual: false
        });
      }
    } else {
      // Set the first day
      this.setState({
        endWindowActive: true,
        startWindowActive: false,
        startDay: day,
        startText: mmnt.format('MMM D, YYYY'),
        endDay: null,
        endText: '',
        selectEndNext: true,
        time: TIME_DEFAULT,
        isDaysEqual: false
      });
    }
  }

  handleTimePickerChange({ period, timeMetric, value }) {
    this.setState((prevState) => ({
      ...prevState,
      time: {
        ...prevState.time,
        [period]: {
          ...prevState.time[period],
          [timeMetric]: value
        }
      }
    }));
  }

  setTime = () => {
    this.setState((prevState) => ({
      ...prevState,
      time: getTime(prevState.time)
    }));
  };

  dayChanges = (prevState) => {
    if (!prevState.startDay && !prevState.endDay) {
      return;
    }

    const isPreviousDaysEqual = datesComparison(prevState.startDay, prevState.endDay);
    const isCurrentDaysEqual = datesComparison(this.state.startDay, this.state.endDay);

    if (isPreviousDaysEqual) {
      // Equal days have already selected
      return;
    }

    if (isCurrentDaysEqual && !this.state.isDaysEqual) {
      // Equal days are selected currently
      this.setState((prevState) => ({
        ...prevState,
        time: TIME_FOR_EQUAL_DAYS,
        isDaysEqual: true
      }));

      return;
    }

    if (!isCurrentDaysEqual) {
      // Not equal days are selected currently
      this.setState((prevState) => ({
        ...prevState,
        isDaysEqual: false
      }));
    }
  };

  setDayError = (timePeriod, hasError) => {
    if (timePeriod === TIME_PERIOD.START) {
      this.setState({ isStartDayError: hasError });
    } else {
      this.setState({ isEndDayError: hasError });
    }
  };

  validateDateRange = (startDate, endDate) => {
    // Return true if either date is missing - validation will happen when both exist
    if (!startDate || !endDate) return true;
    return moment(endDate).startOf('day').isSameOrAfter(moment(startDate).startOf('day'));
  };

  onChangeHandler = (evt, timePeriod) => {
    const inputValue = evt.target.value.trim();
    let newState = {};

    // Update text state immediately
    if (timePeriod === TIME_PERIOD.START) {
      newState.startText = inputValue;
    } else {
      newState.endText = inputValue;
    }

    // Handle empty input
    if (!inputValue) {
      this.setDayError(timePeriod, false);
      if (timePeriod === TIME_PERIOD.START) {
        newState.startDay = null;
        // Clear end date error if start date is cleared and end date exists
        if (this.state.endDay) {
          this.setDayError(TIME_PERIOD.END, false);
        }
      } else {
        newState.endDay = null;
      }
      this.setState((prevState) => ({
        ...prevState,
        ...newState
      }));
      return;
    }

    // Validate date format and value
    try {
      const mmnt = moment(inputValue, 'MM/DD/YYYY', true);
      if (!mmnt.isValid()) {
        this.setDayError(timePeriod, true);
      } else {
        const newDate = mmnt.toDate();
        
        // Determine which dates to validate
        const startDate = timePeriod === TIME_PERIOD.START ? newDate : this.state.startDay;
        const endDate = timePeriod === TIME_PERIOD.END ? newDate : this.state.endDay;
        
        // Update state based on validation
        if (timePeriod === TIME_PERIOD.START) {
          newState.startDay = newDate;
          this.setDayError(TIME_PERIOD.START, false);
          
          // Validate end date if it exists
          if (this.state.endDay) {
            const isEndDateValid = this.validateDateRange(newDate, this.state.endDay);
            this.setDayError(TIME_PERIOD.END, !isEndDateValid);
          }
        } else {
          newState.endDay = newDate;
          const isRangeValid = this.validateDateRange(this.state.startDay, newDate);
          this.setDayError(TIME_PERIOD.END, !isRangeValid);
        }
      }
    } catch (error) {
      this.setDayError(timePeriod, true);
    }

    this.setState((prevState) => ({
      ...prevState,
      ...newState
    }));
  };

  onBlurHandler = (evt, timePeriod) => {
    const inputValue = evt.target.value.trim();
    let newState = {};

    // Handle empty input on blur
    if (!inputValue) {
      this.setDayError(timePeriod, false);
      if (timePeriod === TIME_PERIOD.START) {
        newState.startDay = null;
        newState.startText = '';
        if (this.state.endDay) {
          this.setDayError(TIME_PERIOD.END, false);
        }
      } else {
        newState.endDay = null;
        newState.endText = '';
      }
      this.setState((prevState) => ({
        ...prevState,
        ...newState
      }));
      return;
    }

    try {
      const mmnt = moment(inputValue, 'MM/DD/YYYY', true);
      if (!mmnt.isValid()) {
        this.setDayError(timePeriod, true);
      } else {
        const newDate = mmnt.toDate();
        const formattedDate = mmnt.format('MM/DD/YYYY');

        if (timePeriod === TIME_PERIOD.START) {
          newState.startDay = newDate;
          newState.startText = formattedDate;
          this.setDayError(TIME_PERIOD.START, false);

          // Validate end date if it exists
          if (this.state.endDay) {
            const isEndDateValid = this.validateDateRange(newDate, this.state.endDay);
            this.setDayError(TIME_PERIOD.END, !isEndDateValid);
          }
        } else {
          newState.endDay = newDate;
          newState.endText = formattedDate;
          const isRangeValid = this.validateDateRange(this.state.startDay, newDate);
          this.setDayError(TIME_PERIOD.END, !isRangeValid);
        }
      }
    } catch (error) {
      this.setDayError(timePeriod, true);
    }

    this.setState((prevState) => ({
      ...prevState,
      ...newState
    }));
  };

  componentDidUpdate(_, prevState) {
    if (this.props.isTimeIncluded) {
      this.dayChanges(prevState);
      return;
    }

    var now = new Date();
    if (this.state.startDay > now && this.state.endDay > now) {
      this.setState({ isDateOutOfBounds: true });
    }
  }

  componentDidMount() {
    let startDay = moment(this.props.startDay);
    let endDay = moment(this.props.endDay);

    let startDayDate = startDay.toDate();
    let endDayDate = endDay.toDate();

    startDayDate.setHours(0, 0, 0, 0);
    endDayDate.setHours(0, 0, 0, 0);

    if (this.props.startDay) {
      this.setState({
        startDay: startDayDate,
        startText: startDay.format('MM/DD/YYYY')
      });
    }

    if (this.props.endDay) {
      this.setState({
        endDay: endDayDate,
        endText: endDay.format('MM/DD/YYYY')
      });
    }

    // TimePicker setting
    if (this.props.isTimeIncluded) {
      const startDayTime = this.props.startDay ? new Date(this.props.startDay) : null;
      const endDayTime = this.props.endDay ? new Date(this.props.endDay) : null;

      this.setState((prevState) => ({
        ...prevState,
        time: getPropsTime(startDayTime, endDayTime)
      }));
    }
  }

  render() {
    let startClass = this.state.startWindowActive
      ? 'CalendarFilter_Schedule_Start CalendarFilter_Schedule_Active'
      : 'CalendarFilter_Schedule_Start';
    let endClass = this.state.endWindowActive
      ? 'CalendarFilter_Schedule_End CalendarFilter_Schedule_Active'
      : 'CalendarFilter_Schedule_End';

    let lastMonth = new Date();
    lastMonth.setDate(0); //sets to last day of last month
    return (
      <div className="CalendarFilter">
        <div className="provisionsHeader">
          <span className={'provisionsModalTitle'}>Select a day or a range of days</span>
          {!this.props.hideX && (
            <div className={'provisionsModalCloseButtonDiv'}>
              <button type="button" onClick={this.props.close}>
                <SvgAF img={IconLoader.icon_close_x} hover={IconLoader.icon_close_x_hover} />
              </button>
            </div>
          )}
        </div>

        <div className="CalendarFilter_Schedule_Div">
          <div className={startClass}>
            <input
              type="text"
              className="CalendarFilter_Schedule_Div_Start_Text"
              placeholder={'mm/dd/yyyy'}
              value={this.state.startText}
              onChange={(evt) => this.onChangeHandler(evt, TIME_PERIOD.START)}
              onBlur={(evt) => this.onBlurHandler(evt, TIME_PERIOD.START)}
              onFocus={(evt) => {
                let startText = '';
                try {
                  if (this.state.startDay != null) {
                    let mmnt = moment(this.state.startDay);
                    startText = mmnt.format('MM/DD/YYYY');
                  }
                } catch (error) {
                  // console.log('onFocus. error:', error, 'startText:', this.state.startText);
                } finally {
                  this.setState({ startText: startText, startWindowActive: true, endWindowActive: false });
                }
              }}
              name="startTime"
              autoComplete="off"
            />

            {this.state.isStartDayError && <span className="CalendarFilter_Schedule_Start_ErrorMessage">Invalid start date</span>}
          </div>

          {this.props.isTimeIncluded && (
            <div className="CalendarFilter_TimePicker_Wrapper">
              <TimePicker
                time={this.state.time.start}
                period={TIME_PERIOD.START}
                setTime={this.setTime}
                onChange={this.handleTimePickerChange}
              />
            </div>
          )}

          <div className={'CalendarFilter_Schedule_Dash'}>-</div>

          <div className={endClass}>
            <input
              type="text"
              className="CalendarFilter_Schedule_Div_End_Text"
              placeholder={'mm/dd/yyyy'}
              value={this.state.endText}
              onChange={(evt) => this.onChangeHandler(evt, TIME_PERIOD.END)}
              onBlur={(evt) => this.onBlurHandler(evt, TIME_PERIOD.END)}
              onFocus={(evt) => {
                let endText = '';
                try {
                  if (this.state.endDay != null) {
                    let mmnt = moment(this.state.endDay);
                    endText = mmnt.format('MM/DD/YYYY');
                  }
                } catch (error) {
                  // console.log('on Focus. error:', error, 'endText:', this.state.endText);
                } finally {
                  this.setState({ endText: endText, startWindowActive: false, endWindowActive: true });
                }
              }}
              name="endTime"
              autoComplete="off"
            />

            {this.state.isEndDayError && <span className="CalendarFilter_Schedule_End_ErrorMessage">Invalid end date</span>}
          </div>

          {this.props.isTimeIncluded && (
            <div className="CalendarFilter_TimePicker_Wrapper">
              <TimePicker
                time={this.state.time.end}
                period={TIME_PERIOD.END}
                setTime={this.setTime}
                onChange={this.handleTimePickerChange}
              />
            </div>
          )}
        </div>
        <DayPicker
          className="provisionCalendar"
          numberOfMonths={2}
          selectedDays={[this.state.startDay, { from: this.state.startDay, to: this.state.endDay }]}
          modifiers={{ start: this.state.startDay, end: this.state.endDay }}
          onDayClick={this.handleDayClick}
          month={lastMonth}
        />
        {this.state.isDateOutOfBounds && (
          <div className="provisionsErrorBox">Please set the beginning date to either today or one in the past</div>
        )}
        <div className="provisionsFooter">
          <div id="zone">Time zone: UTC{getTimeZone()}</div>

          <div className="provisionsSelectionButtons">
            <button type="button" className="provisionsClearButton" onClick={this.handleClear}>
              Clear
            </button>
            <button
              type="button"
              className="provisionsApplyButton"
              disabled={this.state.isDateOutOfBounds || this.state.startDay == null}
              onClick={this.handleSubmit}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default DualCalendar;

export const SingleCalendar = (props) => {
  const [isTimeDropdownOpen, setTimeDropdownOpen] = useState(false);
  const [isTimeWindowActive, setTimeWindowActive] = useState(false);
  const [dateWindowActive, setDateWindowActive] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date(Date.now()));
  const [dateText, setDateText] = useState(moment().format('MMM D, YYYY'));
  const [timeText, setTimeText] = useState(moment().add(1, 'minute').format('hh:mm a'));
  const [dateValid, isDateValid] = useState(true);
  const [isClear, setIsClear] = useState(false);

  const mounted = useRef();
  useEffect(() => {
    if (props.inputTimeText && props.inputTimeText) {
      setDateText(props.inputDateText);
      setTimeText(props.inputTimeText);
      setSelectedDay(new Date(moment(props.inputDateText + ' ' + props.inputTimeText)));
    }
  }, [props.inputTimeText, props.inputDateText]);

  useEffect(() => {
    if (!mounted.current && !props.inputDateText && !props.inputTimeText) {
      // do componentDidMount logic
      props.sendDate && props.sendDate(dateText);
      props.sendRawDate && props.sendRawDate(selectedDay);
      props.sendTime && props.sendTime(timeText);
      props.defaultTime && setTimeText(props.defaultTime);
      mounted.current = true;
    } else {
      let datetime = dateText ? moment(dateText) : null;
      let now = moment();

      if (props.alarmHistory) {
        const isAlarmDateValid = datetime.isSameOrBefore(now, 'day');
        if (isAlarmDateValid !== dateValid) {
          isDateValid(isAlarmDateValid);
          props.isDateValid(isAlarmDateValid);
        }

        return;
      }
      if (datetime) {
        let mmntTime = moment(timeText, 'hh:mm a');
        datetime.hour(mmntTime.hour());
        datetime.minute(mmntTime.minute());
      }

      let isValid = true;

      if (datetime !== null && datetime !== '')
        isValid = props.timeMode ? datetime.isAfter(now, 'minute') : !datetime.isBefore(now, 'day');

      if (dateValid !== isValid) {
        isDateValid(isValid);
        props.isDateValid(isValid);
      }

      if (isClear === false && props.doClear === true) {
        setTimeWindowActive(false);
        setDateWindowActive(false);
        setSelectedDay(null);
        setDateText('');
        setTimeText('');
        props.sendDate && props.sendDate(null);
        props.sendTime && props.sendTime(null);
        props.sendRawDate && props.sendRawDate(null);
        setIsClear(props.doClear);
      }
    }
  }, [props, dateText, selectedDay, timeText, dateValid, isClear]);

  useEffect(() => {
    setTimeDropdownOpen(isTimeWindowActive);
  }, [isTimeWindowActive]);

  function handleDayClick(day) {
    // console.log('day:', day.toISOString());
    let mmnt = moment(day.toISOString());
    //this.setState({dateWindowActive: true, isTimeWindowActive:false, selectedDay: day, dateText:mmnt.format("MMM D, YYYY")});
    let datetext = mmnt.format('MMM D, YYYY');

    setTimeWindowActive(false);
    setDateWindowActive(true);
    setSelectedDay(day);
    setDateText(datetext);
    // console.log('schyy', props, props.sendDate && true, datetext);
    if (props.sendDate) props.sendDate(datetext);
    props.sendRawDate && props.sendRawDate(day);
    setIsClear(false);
  }

  // if (props.onClear && dateWindowActive || (isTimeWindowActive != null || selectedDay != null)) {
  //     setTimeWindowActive(false);
  //     setDateWindowActive(false);
  //     setSelectedDay(null);
  // }

  const timeModalRef = useRef();

  let dateClass = dateWindowActive
    ? 'SingleCalendar_Schedule_Date SingleCalendar_Schedule_Active'
    : 'SingleCalendar_Schedule_Date';
  let timeClass = isTimeWindowActive
    ? 'SingleCalendar_Schedule_Time SingleCalendar_Schedule_Active'
    : 'SingleCalendar_Schedule_Time';

  let buttons2 = (
    <div className={'SingleCalendar_Schedule_Div'}>
      <div className="SingleCalendar_Schedule_Div_Left">
        <div className={dateClass}>
          <input
            type="text"
            className="SingleCalendar_Schedule_Div_Left_Text"
            placeholder={'mm/dd/yyyy'}
            value={dateText}
            onChange={(evt) => {
              let newState = {};
              try {
                let mmnt = moment(evt.target.value, 'MM/DD/YYYY'); //try to parse text as date
                console.log('mmnt:', mmnt.format());
                let newDate = mmnt.toDate();
                console.log('New Day', newDate);
                if (isNaN(newDate)) {
                  throw new Error('invalid date');
                }
                newState.selectedDay = newDate;
              } catch (error) {
                console.log('Error:', error, 'Input:', evt.target.value);
              } finally {
                newState.dateText = evt.target.value;
                //this.setState(newState);

                setSelectedDay(newState.selectedDay);
                setDateText(newState.dateText);
                if (props.sendDate) props.sendDate(newState.dateText);
                props.sendRawDate && props.sendRawDate(newState.selectedDay);
              }
            }}
            onFocus={(evt) => {
              let dateText;
              try {
                let mmnt = moment(selectedDay);
                dateText = mmnt.format('MM/DD/YYYY');
              } catch (error) {
                console.log('error:', error, 'dateText:', dateText);
              } finally {
                //this.setState({dateText:dateText, dateWindowActive: true, isTimeWindowActive: false});
                setDateText(dateText);
                setTimeWindowActive(false);
                setDateWindowActive(true);
                setIsClear(false);
                if (props.sendDate) props.sendDate(dateText);
                props.sendRawDate && props.sendRawDate(selectedDay);
              }
            }}
          />
        </div>
        {props.timeMode && (
          <div className={timeClass} ref={timeModalRef}>
            <input
              type="text"
              className="SingleCalendar_Schedule_Div_Left_Text"
              placeholder={'hh:mm'}
              value={timeText}
              onChange={(evt) => {
                setTimeDropdownOpen(false);
                let newState = {};
                let mmnt = moment(evt.target.value, 'hh:mm a'); //try to parse text as time
                console.log('mmnt time:', mmnt.format());
                newState.timeText = evt.target.value;
                //this.setState(newState);

                setTimeText(newState.timeText);
                if (props.sendTime) props.sendTime(newState.timeText);
              }}
              onFocus={(evt) => {
                //this.setState({dateWindowActive: false, isTimeWindowActive: true});
                setTimeWindowActive(true);
                setDateWindowActive(false);
                setIsClear(false);
              }}
            />
            {isTimeDropdownOpen ? (
              <div
                // onBlur={() => {}}
                className="SingleCalendar_Schedule_Time_Modal_Div"
              >
                <ul className="SingleCalendar_Schedule_Time_List Global_Modal_Shadow">
                  <PerfectScrollbar>
                    {Object.keys(hour_list).map((item) => (
                      <li
                        key={item}
                        onClick={() => {
                          //this.selectTimeOnClick(item);

                          //this.setState({timeText: time,
                          setTimeWindowActive(false);
                          setTimeText(item);
                          if (props.sendTime) props.sendTime(item);
                          setIsClear(false);
                        }}
                      >
                        <div>{item}</div>
                      </li>
                    ))}
                  </PerfectScrollbar>
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  let calender = (
    <div className="SingleCalendarDiv">
      {buttons2}
      {!dateValid ? (
        <div className="SoftwareDownloadModal_InvalidTime_Div">
          <div className="SoftwareDownloadModal_InvalidTime_Icon_Div">
            <img src={IconLoader.icon_error_s} alt="error icon" />
          </div>
          <div className="SoftwareDownloadModal_InvalidTime_Text">Selected date/time is invalid</div>
        </div>
      ) : null}
      <DayPicker
        className="SoftwareDownloadScheduler_DayPicker"
        numberOfMonths={1}
        selectedDays={selectedDay}
        month={selectedDay}
        onDayClick={handleDayClick}
      />
    </div>
  );

  return <div>{calender}</div>;
};
